import { useEffect, useMemo, useState } from "react";
import { api, extractError } from "@/lib/api";
import { useBrands } from "@/contexts/BrandContext";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Select, SelectTrigger, SelectValue, SelectContent, SelectItem,
} from "@/components/ui/select";
import {
  Card, CardContent, CardHeader, CardTitle,
} from "@/components/ui/card";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import {
  Ban, RefreshCw, Sparkles, Layers, AlertTriangle, Search, ArrowRight,
} from "lucide-react";
import { toast } from "sonner";
import { InfoTip } from "@/components/app/InfoTip";

export default function CategoryNegatives() {
  const { user } = useAuth();
  const { selectedBrandId, selectedBrand } = useBrands();
  const isAdmin = user?.role === "admin";
  const canApply = ["admin", "brand_manager"].includes(user?.role);

  const [lexicon, setLexicon] = useState({ items: [], total: 0, categories: [] });
  const [category, setCategory] = useState("");
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(false);
  const [recomputing, setRecomputing] = useState(false);

  const [suggestions, setSuggestions] = useState(null);
  const [suggestionsLoading, setSuggestionsLoading] = useState(false);
  const [applying, setApplying] = useState(false);

  const loadLexicon = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ limit: "500" });
      if (category) params.set("category", category);
      const { data } = await api.get(`/category-negatives?${params}`);
      setLexicon(data);
    } catch (e) {
      toast.error(extractError(e, "Could not load lexicon"));
    } finally { setLoading(false); }
  };
  useEffect(() => { loadLexicon(); /* eslint-disable-next-line */ }, [category]);

  const loadSuggestions = async () => {
    if (!selectedBrandId) { setSuggestions(null); return; }
    setSuggestionsLoading(true);
    try {
      const { data } = await api.get(`/category-negatives/suggestions?brand_id=${selectedBrandId}&limit=200`);
      setSuggestions(data);
    } catch (e) {
      setSuggestions(null);
      toast.error(extractError(e, "Could not load suggestions"));
    } finally { setSuggestionsLoading(false); }
  };
  useEffect(() => { loadSuggestions(); /* eslint-disable-next-line */ }, [selectedBrandId]);

  const recompute = async () => {
    if (!isAdmin) return;
    setRecomputing(true);
    try {
      const params = new URLSearchParams();
      if (category) params.set("category", category);
      const { data } = await api.post(`/category-negatives/recompute?${params}`);
      toast.success(`Recomputed: +${data.inserted} new · ↻${data.updated} updated · ⤬${data.removed} removed`);
      await loadLexicon();
      await loadSuggestions();
    } catch (e) {
      toast.error(extractError(e, "Recompute failed"));
    } finally { setRecomputing(false); }
  };

  const apply = async () => {
    if (!selectedBrandId || !canApply) return;
    if (!window.confirm(`Mark ${suggestions?.items?.length || 0} terms as negative on ${selectedBrand?.brand_name || "this brand"}'s latest upload? Manual scope overrides will be preserved.`)) return;
    setApplying(true);
    try {
      const { data } = await api.post(`/category-negatives/apply?brand_id=${selectedBrandId}`);
      toast.success(`Applied: ${data.updated} term${data.updated === 1 ? "" : "s"} flipped to negative on upload ${(data.upload_id || "").slice(0, 8)}…`);
      await loadSuggestions();
    } catch (e) {
      toast.error(extractError(e, "Apply failed"));
    } finally { setApplying(false); }
  };

  const filteredLexicon = useMemo(() => {
    if (!search) return lexicon.items;
    const q = search.toLowerCase();
    return lexicon.items.filter((it) => (it.normalized_term || "").toLowerCase().includes(q));
  }, [lexicon, search]);

  return (
    <div className="space-y-6" data-testid="category-negatives-page">
      <div className="flex items-end justify-between">
        <div>
          <p className="text-[11px] uppercase tracking-[0.2em] text-slate-400">Reports · cross-brand learning</p>
          <h1 className="text-3xl font-bold tracking-tight text-slate-900 inline-flex items-center gap-2">
            Category negatives <InfoTip term="CATEGORY_NEG" />
          </h1>
          <p className="mt-1 text-sm text-slate-500 max-w-2xl">
            Terms that multiple brands within the same category have already
            classified as <span className="font-semibold text-rose-700">out of scope</span>.
            New brands inherit this category-level wisdom on day one — no need
            to re-learn the same negatives every time you onboard a SKU.
          </p>
        </div>
        {isAdmin && (
          <Button data-testid="cat-neg-recompute" variant="outline" onClick={recompute} disabled={recomputing} className="gap-1.5">
            <RefreshCw className={`h-4 w-4 ${recomputing ? "animate-spin" : ""}`} />
            {recomputing ? "Recomputing…" : "Recompute lexicon"}
          </Button>
        )}
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <KpiCard testid="cat-neg-kpi-total" icon={Ban} tone="rose" label="Lexicon entries" tip="CATEGORY_NEG" value={lexicon.total.toLocaleString()} sub="Terms surfaced across ≥2 brands" />
        <KpiCard testid="cat-neg-kpi-cats" icon={Layers} tone="slate" label="Categories" value={lexicon.categories.length} sub="With learned negatives" />
        <KpiCard testid="cat-neg-kpi-suggest" icon={Sparkles} tone="violet"
          label={selectedBrand ? `For ${selectedBrand.brand_name.slice(0, 18)}` : "For selected brand"}
          value={suggestions ? suggestions.items.length : "—"}
          sub={suggestions
            ? `${(suggestions.excluded || 0)} already classified`
            : "Pick a brand from the top bar"} />
        <div data-testid="cat-neg-kpi-cta" className="bg-gradient-to-br from-rose-900 to-rose-700 rounded-xl p-5 shadow-sm text-white flex flex-col justify-between">
          <div>
            <div className="text-[11px] font-semibold uppercase tracking-wider text-rose-200">Apply learning</div>
            <div className="mt-2 text-sm">
              {!selectedBrandId
                ? "Select a brand to apply its category-level negatives."
                : !suggestions || !suggestions.items.length
                  ? "No new suggestions for this brand."
                  : `Mark ${suggestions.items.length} terms as negative on this brand's latest upload.`}
            </div>
          </div>
          {selectedBrandId && canApply && suggestions?.items?.length > 0 && (
            <Button
              data-testid="cat-neg-apply"
              size="sm"
              onClick={apply}
              disabled={applying}
              className="mt-3 w-full bg-white text-rose-900 hover:bg-rose-50"
            >
              {applying ? "Applying…" : "Apply to brand"} <ArrowRight className="h-3.5 w-3.5 ml-1" />
            </Button>
          )}
        </div>
      </div>

      {/* Per-brand suggestions */}
      {selectedBrandId && (
        <Card data-testid="cat-neg-suggestions-card">
          <CardHeader className="border-b border-slate-100">
            <CardTitle className="text-lg flex items-center gap-2">
              <Sparkles className="h-4 w-4 text-violet-600" />
              Suggested negatives for {selectedBrand?.brand_name}
            </CardTitle>
          </CardHeader>
          <CardContent className="pt-4">
            {suggestionsLoading ? (
              <div className="text-sm text-slate-500 py-6 text-center">Loading…</div>
            ) : !suggestions?.items?.length ? (
              <div className="text-sm text-slate-500 py-6 text-center inline-flex items-center gap-1.5 justify-center w-full">
                <AlertTriangle className="h-4 w-4 text-slate-400" />
                {suggestions?.categories?.length
                  ? "No cross-brand negatives available for this brand's categories yet — needs ≥2 brands per category to start learning."
                  : "This brand has no categories assigned. Add at least one category on the Brands page to enable category learning."}
              </div>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Term</TableHead>
                    <TableHead>Category</TableHead>
                    <TableHead className="text-right">Brands signalling</TableHead>
                    <TableHead className="text-right">Occurrences</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {suggestions.items.slice(0, 30).map((s) => (
                    <TableRow key={`${s.category}-${s.normalized_term}`}>
                      <TableCell className="font-mono text-xs">{s.normalized_term}</TableCell>
                      <TableCell className="text-xs text-slate-600">{s.category}</TableCell>
                      <TableCell className="text-right text-xs font-semibold text-rose-700">{s.brand_count}</TableCell>
                      <TableCell className="text-right text-xs text-slate-700">{s.occurrences}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
            {suggestions?.items?.length > 30 && (
              <div className="mt-2 text-[11px] text-slate-500 text-center">
                Showing top 30 of {suggestions.items.length} — apply will use the full list.
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* Full lexicon */}
      <Card>
        <CardHeader className="border-b border-slate-100">
          <div className="flex items-center justify-between flex-wrap gap-3">
            <CardTitle className="text-lg">Full lexicon</CardTitle>
            <div className="flex items-center gap-2">
              <div className="relative w-64">
                <Search className="h-3.5 w-3.5 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                <Input data-testid="cat-neg-search" value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search terms…" className="pl-9 h-9 text-sm" />
              </div>
              <Select value={category || "__all__"} onValueChange={(v) => setCategory(v === "__all__" ? "" : v)}>
                <SelectTrigger data-testid="cat-neg-filter-category" className="w-[220px] h-9 text-sm"><SelectValue placeholder="All categories" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="__all__">All categories</SelectItem>
                  {lexicon.categories.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          </div>
        </CardHeader>
        <CardContent className="pt-4">
          {loading ? (
            <div className="text-sm text-slate-500 py-6 text-center">Loading…</div>
          ) : !filteredLexicon.length ? (
            <div className="text-sm text-slate-500 py-10 text-center">
              No category negatives yet. Need at least 2 brands in the same category with overlapping out-of-scope terms.
              {isAdmin && <span> Click <b>Recompute lexicon</b> after onboarding more brands.</span>}
            </div>
          ) : (
            <Table data-testid="cat-neg-lexicon-table">
              <TableHeader>
                <TableRow>
                  <TableHead>Term</TableHead>
                  <TableHead>Category</TableHead>
                  <TableHead className="text-right">Brands</TableHead>
                  <TableHead className="text-right">Occurrences</TableHead>
                  <TableHead>Sample variants</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredLexicon.slice(0, 200).map((it) => (
                  <TableRow key={`${it.category}-${it.normalized_term}`}>
                    <TableCell className="font-mono text-xs">{it.normalized_term}</TableCell>
                    <TableCell className="text-xs text-slate-600">{it.category}</TableCell>
                    <TableCell className="text-right text-xs font-semibold text-rose-700">{it.brand_count}</TableCell>
                    <TableCell className="text-right text-xs text-slate-700">{it.occurrences}</TableCell>
                    <TableCell className="text-[10px] text-slate-500 max-w-[300px] truncate" title={(it.sample_terms || []).join(", ")}>
                      {(it.sample_terms || []).slice(0, 3).join(" · ")}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
          {filteredLexicon.length > 200 && (
            <div className="mt-2 text-[11px] text-slate-500 text-center">
              Showing top 200 — use the search box or category filter to narrow.
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

function KpiCard({ icon: Icon, label, value, sub, tone = "slate", testid, tip }) {
  const tones = {
    slate: "text-slate-900",
    rose: "text-rose-700",
    violet: "text-violet-700",
  };
  return (
    <div data-testid={testid} className="bg-white border border-slate-200 rounded-xl p-5 shadow-sm">
      <div className="flex items-center justify-between">
        <span className="text-[11px] font-semibold uppercase tracking-wider text-slate-500 inline-flex items-center gap-1">
          {label}{tip ? <InfoTip term={tip} /> : null}
        </span>
        <Icon strokeWidth={1.5} className="h-4 w-4 text-slate-400" />
      </div>
      <div className={`mt-2 text-3xl font-bold tracking-tight ${tones[tone]}`}>{value}</div>
      {sub ? <div className="mt-1 text-xs text-slate-500">{sub}</div> : null}
    </div>
  );
}
